import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { eventsApi, adminApi } from '../services/api';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'seminar',
    date: '',
    time: '10:00',
    publishWeeks: 1,
    publishPrice: 499,
    mode: 'online',
    location: '',
    videoUrl: '',
    videoFileName: '',
    videoFileSize: 0,
  });

  const [loading, setLoading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const calculateCalculatedEnd = () => {
    if (!formData.date) return '';
    try {
      const start = new Date(`${formData.date}T${formData.time || '10:00'}`);
      if (isNaN(start.getTime())) return '';
      const w = Math.max(1, parseInt(formData.publishWeeks, 10) || 1);
      const end = new Date(start.getTime() + w * 7 * 24 * 60 * 60 * 1000);
      return end.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingVideo(true);
      setVideoUploadError('');

      const fd = new FormData();
      fd.append('video', file);

      const res = await adminApi.uploadEventVideo(fd);
      if (res?.url) {
        setFormData((prev) => ({
          ...prev,
          videoUrl: res.url,
          videoFileName: res.fileName || file.name,
          videoFileSize: res.fileSize || file.size,
        }));
      } else {
        throw new Error(res?.message || 'Failed to upload video');
      }
    } catch (err) {
      console.error('Video upload error:', err);
      setVideoUploadError(err.message || 'Failed to upload video file');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const handleRemoveVideo = () => {
    setFormData((prev) => ({
      ...prev,
      videoUrl: '',
      videoFileName: '',
      videoFileSize: 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim()) {
      setError('Please enter an event title');
      return;
    }
    if (!formData.date) {
      setError('Please select a start date');
      return;
    }

    try {
      setLoading(true);
      const startDateTime = new Date(`${formData.date}T${formData.time || '10:00'}`).toISOString();
      const weeksNum = Math.max(1, parseInt(formData.publishWeeks, 10) || 1);
      const computedEnd = new Date(new Date(startDateTime).getTime() + weeksNum * 7 * 24 * 60 * 60 * 1000).toISOString();

      await eventsApi.addEvent({
        title: formData.title.trim(),
        description: formData.description.trim(),
        eventType: formData.eventType,
        mode: formData.mode,
        publishWeeks: weeksNum,
        publishPrice: formData.publishPrice,
        scheduledAt: startDateTime,
        endsAt: computedEnd,
        location: formData.mode === 'offline' ? formData.location.trim() : '',
        videoUrl: formData.videoUrl ? formData.videoUrl.trim() : '',
        videoFileName: formData.videoFileName ? formData.videoFileName.trim() : '',
        videoFileSize: parseInt(formData.videoFileSize, 10) || 0,
      });

      setSuccess('Event created and submitted for review successfully!');
      setTimeout(() => {
        navigate('/physio-dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error creating event:', err);
      setError(err.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const calculatedEndDateStr = calculateCalculatedEnd();

  return (
    <>
      <DashboardSidebar userRole="physiotherapist" />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Link to="/physio-dashboard" style={{ color: '#667eea', textDecoration: 'none', marginBottom: '20px', display: 'inline-block', fontWeight: 600 }}>
            ← Back to Dashboard
          </Link>
          <h1 style={{ fontSize: '32px', color: '#1e293b', margin: '10px 0 6px 0', fontWeight: 800 }}>🎉 Create New Event</h1>
          <p style={{ color: '#64748b', marginBottom: '28px', fontSize: '15px' }}>Organize workshops, webinars, and therapeutic group sessions</p>

          {error && (
            <div style={{ padding: '14px 18px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', marginBottom: '20px', fontWeight: 600 }}>
              ⚠️ {error}
            </div>
          )}

          {success && (
            <div style={{ padding: '14px 18px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '10px', color: '#065f46', marginBottom: '20px', fontWeight: 600 }}>
              ✅ {success}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Event Title *</label>
              <input
                type="text"
                placeholder="e.g. Mindfulness & Stress Reduction Workshop"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Category *</label>
                <select
                  value={formData.eventType}
                  onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', background: '#fff' }}
                >
                  <option value="seminar">🎤 Seminar</option>
                  <option value="workshop">🛠️ Workshop</option>
                  <option value="event">🎉 Special Event</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Session Mode *</label>
                <select
                  value={formData.mode}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', background: '#fff' }}
                >
                  <option value="online">💻 Online (Web Video Room)</option>
                  <option value="offline">🏥 Offline (In-Person Clinic)</option>
                </select>
              </div>
            </div>

            {formData.mode === 'offline' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Clinic / Venue Address *</label>
                <input
                  type="text"
                  placeholder="Enter full address of the venue..."
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
            )}

            {/* Video File Upload */}
            <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1.5px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px', margin: 0 }}>
                  🎥 Upload Event Video / Media File
                </label>
                {uploadingVideo && <span style={{ fontSize: '12px', color: '#7c3aed', fontWeight: 700 }}>⏳ Uploading...</span>}
              </div>

              {videoUploadError && (
                <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '12.5px', marginBottom: '10px' }}>
                  ⚠️ {videoUploadError}
                </div>
              )}

              {formData.videoUrl ? (
                <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '22px' }}>🎬</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#065f46' }}>{formData.videoFileName || 'Attached Video'}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {formData.videoFileSize ? `${(formData.videoFileSize / (1024 * 1024)).toFixed(2)} MB • ` : ''}
                        <a href={formData.videoUrl} target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: 600 }}>
                          Preview File ↗
                        </a>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveVideo}
                    style={{ padding: '5px 10px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    ✕ Remove
                  </button>
                </div>
              ) : (
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: '10px', padding: '16px', background: '#ffffff', cursor: uploadingVideo ? 'not-allowed' : 'pointer', textAlign: 'center' }}>
                  <input type="file" accept="video/*,.mp4,.mov,.webm,.mkv,.avi,.pdf" onChange={handleVideoUpload} disabled={uploadingVideo} style={{ display: 'none' }} />
                  <span style={{ fontSize: '24px', marginBottom: '4px' }}>📁</span>
                  <span style={{ fontWeight: 700, color: '#475569', fontSize: '13.5px' }}>{uploadingVideo ? 'Uploading...' : 'Click or Drag to Upload Video (.mp4, .webm, etc.)'}</span>
                </label>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Event Description</label>
              <textarea
                placeholder="Describe key topics, expected audience, requirements..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="4"
                style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
              />
            </div>

            {/* Publish Weeks Duration Options */}
            <div style={{ marginBottom: '22px', padding: '18px', background: '#f8fafc', borderRadius: '12px', border: '1.5px solid #e2e8f0' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>
                🗓️ Choose Publish Duration (Weeks Option):
              </label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {[
                  { weeks: 1, label: '1 Week (7 Days)', price: 499 },
                  { weeks: 2, label: '2 Weeks (14 Days)', price: 899 },
                  { weeks: 3, label: '3 Weeks (21 Days)', price: 1299 },
                  { weeks: 4, label: '4 Weeks / 1 Month', price: 1699 },
                ].map((opt) => {
                  const isSelected = parseInt(formData.publishWeeks, 10) === opt.weeks;
                  return (
                    <button
                      key={opt.weeks}
                      type="button"
                      onClick={() => setFormData({ ...formData, publishWeeks: opt.weeks, publishPrice: opt.price })}
                      style={{
                        padding: '9px 16px',
                        borderRadius: '10px',
                        border: isSelected ? '2px solid #7c3aed' : '1.5px solid #cbd5e1',
                        background: isSelected ? '#f5f3ff' : '#ffffff',
                        color: isSelected ? '#7c3aed' : '#475569',
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: '13.5px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {opt.label} • ₹{opt.price} {isSelected && '✓'}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Start Date & Time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Start Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Start Time</label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #cbd5e1', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
            </div>

            {/* Auto-Calculated End Date Badge */}
            {calculatedEndDateStr && (
              <div style={{ padding: '12px 16px', background: '#ecfdf5', borderRadius: '10px', border: '1px solid #a7f3d0', marginBottom: '24px', fontSize: '13.5px', color: '#065f46', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>✨</span>
                <span>
                  Active on platform from <strong>{formData.date}</strong> until <strong>{calculatedEndDateStr}</strong> ({formData.publishWeeks} Week{formData.publishWeeks > 1 ? 's' : ''} total).
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '14px' }}>
              <button
                type="submit"
                disabled={loading || uploadingVideo}
                style={{
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '15px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(124, 58, 237, 0.3)',
                }}
              >
                {loading ? 'Publishing Event...' : '🚀 Submit Event for Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateEventPage;
